import React, { useState, useRef, useCallback } from 'react';
import ReactCrop, { centerCrop, makeAspectCrop } from 'react-image-crop';
import 'react-image-crop/dist/ReactCrop.css';

const ImageCropModal = ({ onSave, onCancel, isUploading }) => {
  const [selectedImage, setSelectedImage] = useState(null);
  const [crop, setCrop] = useState();
  const [completedCrop, setCompletedCrop] = useState();
  const imgRef = useRef(null);
  const fileInputRef = useRef(null);

  const onSelectFile = (e) => {
    if (e.target.files && e.target.files.length > 0) {
      const file = e.target.files[0];
      
      // Validate file type
      const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp'];
      if (!allowedTypes.includes(file.type)) {
        alert('Please select a JPG, PNG, or WebP image file.');
        return;
      }

      // Validate file size (10MB)
      if (file.size > 10 * 1024 * 1024) {
        alert('File too large. Please select an image smaller than 10MB.');
        return;
      }

      const reader = new FileReader();
      reader.addEventListener('load', () => {
        setSelectedImage(reader.result?.toString() || '');
      });
      reader.readAsDataURL(file);
    }
  };

  const onImageLoad = useCallback((e) => {
    const { width, height } = e.currentTarget;
    
    // Create a circular crop in the center
    const crop = centerCrop(
      makeAspectCrop(
        {
          unit: '%',
          width: 80,
        },
        1, // 1:1 aspect ratio for circular crop
        width,
        height
      ),
      width,
      height
    );
    
    setCrop(crop);
  }, []);

  const getCroppedImg = useCallback(() => {
    if (!completedCrop || !imgRef.current) {
      return null;
    }

    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    const image = imgRef.current;

    if (!ctx) {
      return null;
    }

    const scaleX = image.naturalWidth / image.width;
    const scaleY = image.naturalHeight / image.height;

    canvas.width = completedCrop.width * scaleX;
    canvas.height = completedCrop.height * scaleY;

    ctx.drawImage(
      image,
      completedCrop.x * scaleX,
      completedCrop.y * scaleY,
      completedCrop.width * scaleX,
      completedCrop.height * scaleY,
      0,
      0,
      canvas.width,
      canvas.height
    );

    return new Promise((resolve) => {
      canvas.toBlob(
        (blob) => {
          resolve(blob);
        },
        'image/jpeg',
        0.85
      );
    });
  }, [completedCrop]);

  const handleSave = async () => {
    try {
      const croppedImageBlob = await getCroppedImg();
      if (croppedImageBlob) {
        onSave(croppedImageBlob);
      }
    } catch (error) {
      console.error('Error cropping image:', error);
      alert('Failed to process image. Please try again.');
    }
  };

  return (
    <div className="image-crop-modal">
      <div className="image-crop-modal__backdrop" onClick={onCancel}></div>
      <div className="image-crop-modal__container">
        <div className="image-crop-modal__header">
          <h2>Upload Profile Picture</h2>
          <button 
            className="image-crop-modal__close-btn"
            onClick={onCancel}
            disabled={isUploading}
          >
            ✕
          </button>
        </div>

        <div className="image-crop-modal__content">
          {!selectedImage ? (
            <div className="image-crop-modal__file-select">
              <input
                type="file"
                ref={fileInputRef}
                accept="image/jpeg,image/jpg,image/png,image/webp"
                onChange={onSelectFile}
                style={{ display: 'none' }}
              />
              <div 
                className="image-crop-modal__drop-zone"
                onClick={() => fileInputRef.current?.click()}
              >
                <div className="image-crop-modal__drop-icon">📷</div>
                <div className="image-crop-modal__drop-text">
                  <p>Click to select an image</p>
                  <p className="image-crop-modal__drop-subtext">
                    JPG, PNG, or WebP • Max 10MB
                  </p>
                </div>
              </div>
            </div>
          ) : (
            <div className="image-crop-modal__crop-area">
              <ReactCrop
                crop={crop}
                onChange={(_, percentCrop) => setCrop(percentCrop)}
                onComplete={(c) => setCompletedCrop(c)}
                aspect={1}
                circularCrop
              >
                <img
                  ref={imgRef}
                  alt="Crop"
                  src={selectedImage}
                  onLoad={onImageLoad}
                  className="image-crop-modal__image"
                />
              </ReactCrop>
              
              <div className="image-crop-modal__preview">
                <div className="image-crop-modal__preview-label">Preview:</div>
                <div className="image-crop-modal__preview-circle">
                  {completedCrop && (
                    <canvas
                      ref={(canvas) => {
                        if (canvas && completedCrop && imgRef.current) {
                          const ctx = canvas.getContext('2d');
                          const image = imgRef.current;
                          const scaleX = image.naturalWidth / image.width;
                          const scaleY = image.naturalHeight / image.height;

                          canvas.width = 100;
                          canvas.height = 100;

                          const sourceWidth = completedCrop.width * scaleX;
                          const sourceHeight = completedCrop.height * scaleY;

                          ctx.drawImage(
                            image,
                            completedCrop.x * scaleX,
                            completedCrop.y * scaleY,
                            sourceWidth,
                            sourceHeight,
                            0,
                            0,
                            100,
                            100
                          );
                        }
                      }}
                      className="image-crop-modal__preview-canvas"
                    />
                  )}
                </div>
              </div>
            </div>
          )}
        </div>

        <div className="image-crop-modal__actions">
          {selectedImage && (
            <button
              className="image-crop-modal__btn image-crop-modal__btn--secondary"
              onClick={() => setSelectedImage(null)}
              disabled={isUploading}
            >
              Choose Different Image
            </button>
          )}
          
          <div className="image-crop-modal__primary-actions">
            <button
              className="image-crop-modal__btn image-crop-modal__btn--cancel"
              onClick={onCancel}
              disabled={isUploading}
            >
              Cancel
            </button>
            
            {selectedImage && completedCrop && (
              <button
                className="image-crop-modal__btn image-crop-modal__btn--save"
                onClick={handleSave}
                disabled={isUploading}
              >
                {isUploading ? 'Uploading...' : 'Save'}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ImageCropModal;